import React, { Component } from "react";
import Select from "react-select";
import Helpers from "../util/helpers";

import IndicatorsViz from "../components/IndicatorsViz";
import { FixedLoadingLabel } from "../components/Loader";
import LegalFooter from "../components/LegalFooter";
import { UsefulLinks } from "./UsefulLinks";
import { withI18n } from "@lingui/react";
import { I18n } from "@lingui/core";
import { t, Trans } from "@lingui/macro";

import "styles/Indicators.css";
import { INDICATORS_DATASETS } from "./IndicatorsDatasets";
import {
  indicatorsInitialState,
  IndicatorsProps,
  IndicatorsState,
  IndicatorsChartShift,
  IndicatorsTimeSpan,
  indicatorsTimeSpans,
  IndicatorsDatasetId,
  indicatorsDatasetIds,
} from "./IndicatorsTypes";
import { NetworkErrorMessage } from "./NetworkErrorMessage";
import { AmplitudeEvent, logAmplitudeEvent } from "./Amplitude";
import { withRouter } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import { AddressPageUrlParams, removeIndicatorSuffix } from "routes";

type TimeSpanTranslationsMap = {
  [K in IndicatorsTimeSpan]: (i18n: I18n) => string;
};

const TIMESPAN_TRANSLATIONS: TimeSpanTranslationsMap = {
  month: (i18n) => i18n._(t`month`),
  quarter: (i18n) => i18n._(t`quarter`),
  year: (i18n) => i18n._(t`year`),
};

/**
 * Generates an appropriate width for a dropdown selection menu
 * that's scaled according to the longest text selection.
 */
const getDropdownWidthFromLongestSelection = (selections: string[]) => {
  const LETTER_WIDTH = 10;
  const MENU_BUFFER = 70;
  const MAX_WIDTH = 350;

  const lengthOfLongestSelection = Math.max(...selections.map((selection) => selection.length));
  return Math.min(lengthOfLongestSelection * LETTER_WIDTH + MENU_BUFFER, MAX_WIDTH);
};

type IndicatorsWithRouterProps = RouteComponentProps<AddressPageUrlParams> & IndicatorsProps;

export const validateIndicatorParam = (indicatorParam?: string) => {
  const indicator = indicatorParam as IndicatorsDatasetId;
  if (indicatorsDatasetIds.includes(indicator)) {
    return indicator;
  }
};

class IndicatorsWithoutI18n extends Component<IndicatorsWithRouterProps, IndicatorsState> {
  constructor(props: IndicatorsWithRouterProps) {
    super(props);
    const indicator =
      validateIndicatorParam(props.match.params.indicator) || indicatorsInitialState.defaultVis;
    const { detailAddr } = this.props.state.context.portfolioData;
    this.state = {
      ...indicatorsInitialState,
      activeVis: indicator,
      defaultVis: indicator,
      activeTimeSpan: indicator === "rentstabilizedunits" ? "year" : "quarter",
      xAxisViewableColumns:
        indicator === "rentstabilizedunits"
          ? detailAddr.rsunitslatestyear - 2007 + 1
          : indicatorsInitialState.xAxisViewableColumns,
    };
    this.handleVisChange = this.handleVisChange.bind(this);
    this.handleTimeSpanChange = this.handleTimeSpanChange.bind(this);
  }

  /** Shifts the X-axis 'left' or 'right', or 'reset' the X-axis to default */
  handleXAxisChange(shift: IndicatorsChartShift) {
    const span = this.state.xAxisViewableColumns;
    const activeVis = this.state.activeVis;
    const labelsArray =
      this.props.state.context.timelineData &&
      this.props.state.context.timelineData[activeVis].labels;

    if (!labelsArray || labelsArray.length < span) {
      return;
    }

    const groupedLabelsLength = Math.floor(labelsArray.length / this.state.monthsInGroup);

    const xAxisMax = Math.max(groupedLabelsLength - span, 0);
    const currentPosition = this.state.xAxisStart;
    const offset = Math.ceil(6 / this.state.monthsInGroup);

    if (shift === "left") {
      const newPosition = Math.max(currentPosition - offset, 0);
      this.setState({
        xAxisStart: newPosition,
      });
    }

    if (shift === "right") {
      const newPosition = Math.min(currentPosition + offset, xAxisMax);
      this.setState({
        xAxisStart: newPosition,
      });
    }

    if (shift === "reset") {
      this.setState({
        xAxisStart: xAxisMax,
      });
    }
  }

  setUrlIndicator(indicator: IndicatorsDatasetId) {
    const timelinePath = removeIndicatorSuffix(this.props.addressPageRoutes.timeline);
    this.props.history.replace(`${timelinePath}/${indicator}`);
  }

  handleVisChange(selectedOption: any) {
    const activeVis = selectedOption.value;

    logAmplitudeEvent(`${activeVis.analyticsName}TimelineTab` as AmplitudeEvent);
    window.gtag("event", `${activeVis.analyticsName}-timeline-tab`);

    if (activeVis === "rentstabilizedunits") {
      this.setState({
        activeTimeSpan: "year",
        monthsInGroup: 12,
      });
    }

    this.setState({ activeVis });
    this.setUrlIndicator(activeVis);
  }

  handleTimeSpanChange(selectedOption: any) {
    const activeTimeSpan = selectedOption.value;
    var monthsInGroup = activeTimeSpan === "quarter" ? 3 : activeTimeSpan === "year" ? 12 : 1;

    logAmplitudeEvent(`${activeTimeSpan}TimelineTab` as AmplitudeEvent);
    window.gtag("event", `${activeTimeSpan}-timeline-tab`);

    this.setState({ activeTimeSpan, monthsInGroup });
  }

  updateData() {
    if (
      this.props.state.matches({ portfolioFound: { timeline: "noData" } }) &&
      this.props.isVisible
    ) {
      this.props.send({ type: "VIEW_TIMELINE" });
    }
  }

  componentDidMount() {
    this.updateData();
    this.handleXAxisChange("reset");
  }

  componentDidUpdate(prevProps: IndicatorsProps, prevState: IndicatorsState) {
    const { state } = this.props;

    this.updateData();

    if (this.props.isVisible && !this.props.location.pathname.includes(this.state.activeVis)) {
      this.setUrlIndicator(this.state.activeVis);
    }
    const newlyLoadedRawData =
      !prevProps.state.matches({ portfolioFound: { timeline: "success" } }) &&
      state.matches({ portfolioFound: { timeline: "success" } }) &&
      state.context.timelineData;

    // reset chart positions when:
    // 1. default dataset loads or
    // 2. when activeTimeSpan changes:

    if (newlyLoadedRawData || prevState.activeTimeSpan !== this.state.activeTimeSpan) {
      this.handleXAxisChange("reset");
    }

    // process sale history data when:
    // 1. default dataset loads or
    // 2. when activeTimeSpan changes

    if (newlyLoadedRawData || prevState.activeTimeSpan !== this.state.activeTimeSpan) {
      const { detailAddr } = this.props.state.context.portfolioData;
      if (detailAddr.lastsaledate && detailAddr.lastsaleacrisid) {
        var lastSaleDate = detailAddr.lastsaledate;
        var lastSaleYear = lastSaleDate.slice(0, 4);
        var lastSaleQuarter =
          lastSaleYear + "-Q" + Math.ceil(parseInt(lastSaleDate.slice(5, 7)) / 3);
        var lastSaleMonth = lastSaleDate.slice(0, 7);

        this.setState({
          lastSale: {
            date: lastSaleDate,
            label:
              this.state.activeTimeSpan === "year"
                ? lastSaleYear
                : this.state.activeTimeSpan === "quarter"
                ? lastSaleQuarter
                : lastSaleMonth,
            documentid: detailAddr.lastsaleacrisid,
          },
        });
      } else {
        this.setState({
          lastSale: indicatorsInitialState.lastSale,
        });
      }
    }
  }

  render() {
    if (
      !(
        this.props.isVisible &&
        this.props.state.matches({ portfolioFound: { timeline: "success" } })
      )
    ) {
      return this.props.state.matches({ portfolioFound: { timeline: "error" } }) ? (
        <NetworkErrorMessage />
      ) : (
        <FixedLoadingLabel />
      );
    } else {
      const { state, send } = this.props;
      const { detailAddr } = state.context.portfolioData;
      const {
        activeVis,
        activeTimeSpan,
        monthsInGroup,
        xAxisStart,
        xAxisViewableColumns,
      } = this.state;
      const activeData = state.context.timelineData[activeVis];

      const xAxisLength = activeData.labels
        ? Math.floor(activeData.labels.length / monthsInGroup)
        : 0;
      const indicatorDataTotal = activeData.values.total
        ? activeData.values.total.reduce((total: number, sum: number) => total + sum)
        : null;

      const i18n = this.props.i18n;

      const datasetSelectionNames = indicatorsDatasetIds.map((datasetId) =>
        INDICATORS_DATASETS[datasetId].name(i18n)
      );
      const timeSpanSelectionNames = indicatorsTimeSpans.map((timeSpan) =>
        TIMESPAN_TRANSLATIONS[timeSpan](i18n)
      );

      const detailAddrStr = `${detailAddr.housenumber} ${Helpers.titleCase(
        detailAddr.streetname
      )}, ${Helpers.titleCase(detailAddr.boro)}`;

      const datasetDescription = INDICATORS_DATASETS[activeVis];

      const indicatorOptions = indicatorsDatasetIds.map((id) => {
        return {
          label: INDICATORS_DATASETS[id].name(i18n),
          value: id,
        };
      });

      const timeSpanOptions = indicatorsTimeSpans.map((id) => {
        return {
          label: TIMESPAN_TRANSLATIONS[id](i18n),
          value: id,
          isDisabled: activeVis === "rentstabilizedunits" && id !== "year",
        };
      });

      return (
        <div className="Page Indicators">
          <div className="Indicators__content Page__content">
            <div className="columns">
              <div className="column col-8 col-lg-12">
                {detailAddr && (
                  <div className="title-card">
                    <h4 className="title">
                      <b>{detailAddrStr}</b>
                    </h4>
                  </div>
                )}
                <div className="Indicators__links">
                  <div
                    className="Indicators__linksContainer"
                    style={{
                      width: `${getDropdownWidthFromLongestSelection(datasetSelectionNames)}px`,
                    }}
                  >
                    <label
                      id="indicator-dropdown-title"
                      className="Indicators__linksTitle text-uppercase"
                      htmlFor="indicator-dropdown"
                      aria-label={`${i18n._(t`Display:`)} ${INDICATORS_DATASETS[activeVis].name(
                        i18n
                      )}`}
                    >
                      <Trans>Display:</Trans>
                    </label>
                    <br />
                    <Select
                      id="indicator-dropdown"
                      className="basic-single"
                      classNamePrefix="select"
                      aria-labelledby="indicator-dropdown-title"
                      name="indicator-type"
                      defaultValue={indicatorOptions.find((i) => i.value === activeVis)}
                      options={indicatorOptions}
                      onChange={this.handleVisChange}
                    />
                  </div>
                  <div
                    className="Indicators__linksContainer"
                    style={{
                      width: `${getDropdownWidthFromLongestSelection(timeSpanSelectionNames)}px`,
                    }}
                  >
                    <label
                      id="timespan-dropdown-title"
                      className="Indicators__linksTitle text-uppercase"
                      htmlFor="timespan-dropdown"
                      aria-label={`${i18n._(t`View by:`)} ${TIMESPAN_TRANSLATIONS[activeTimeSpan](
                        i18n
                      )}`}
                    >
                      <Trans>View by:</Trans>
                    </label>
                    <br />
                    <Select
                      id="timespan-dropdown"
                      className="basic-single"
                      classNamePrefix="select"
                      aria-labelledby="timespan-dropdown-title"
                      name="timespan"
                      defaultValue={
                        activeVis === "rentstabilizedunits"
                          ? timeSpanOptions.find((i) => i.value === "year")
                          : timeSpanOptions.find((i) => i.value === "quarter")
                      }
                      options={timeSpanOptions}
                      onChange={this.handleTimeSpanChange}
                    />
                  </div>
                </div>
                <span className="title viz-title">
                  {datasetDescription &&
                    indicatorDataTotal !== null &&
                    datasetDescription.quantity(i18n, indicatorDataTotal)}
                </span>
                <div className="Indicators__viz">
                  <button
                    aria-label={i18n._(t`Move chart data left.`)}
                    aria-hidden={xAxisStart === 0 || activeTimeSpan === "year"}
                    aria-disabled={xAxisStart === 0 || activeTimeSpan === "year"}
                    className={
                      xAxisStart === 0 || activeTimeSpan === "year"
                        ? "btn btn-off btn-axis-shift"
                        : "btn btn-axis-shift"
                    }
                    onClick={() => {
                      this.handleXAxisChange("left");
                      window.gtag("event", "graph-back-button");
                    }}
                  >
                    ‹
                  </button>
                  <IndicatorsViz state={state} send={send} {...this.state} />
                  <button
                    aria-label={i18n._(t`Move chart data right.`)}
                    aria-hidden={
                      xAxisStart + xAxisViewableColumns >= xAxisLength || activeTimeSpan === "year"
                    }
                    aria-disabled={
                      xAxisStart + xAxisViewableColumns >= xAxisLength || activeTimeSpan === "year"
                    }
                    className={
                      xAxisStart + xAxisViewableColumns >= xAxisLength || activeTimeSpan === "year"
                        ? "btn btn-off btn-axis-shift"
                        : "btn btn-axis-shift"
                    }
                    onClick={() => this.handleXAxisChange("right")}
                  >
                    ›
                  </button>
                </div>
              </div>
              <div className="column column-context col-4 col-lg-12">
                <div className="card">
                  <div className="card-header">
                    <div className="card-title h5">
                      <Trans>What are {datasetDescription && datasetDescription.name(i18n)}?</Trans>
                    </div>
                    <div className="card-subtitle text-gray" />
                  </div>
                  <div className="card-body">
                    {datasetDescription && datasetDescription.explanation(i18n)}
                  </div>
                </div>

                <UsefulLinks addrForLinks={detailAddr} location="timeline-tab" />
              </div>
            </div>
          </div>
          <LegalFooter />
        </div>
      );
    }
  }
}

const Indicators = withRouter(withI18n()(IndicatorsWithoutI18n));
export default Indicators;
